import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import { Observable, of } from 'rxjs';
import * as UserActions from '@store/user/user.actions';
import * as SearchBoardActions from '@store/search-board/search-board.actions';
import { CustomHeadersService } from '@root/search/services/custom-headers.service';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { RibbonService } from '@shared/services/ribbon.service';
import { RIBBON_TYPE } from '@shared/models/ribbon.model';
import { AppState } from '@root/app-state.module';
import { select, Store } from '@ngrx/store';
import {
  selectCardsAttributes,
  selectCardsPreference,
  selectGridHeaders,
  selectPreferenceValue,
  selectTablePreference,
  selectTemplate
} from '@store/user/user.selector';
import { ComponentCode, DataElement, PreferenceCode, SaveGridHeadersResponse, Template, TemplateCode } from '@store/user/user.model';
import { HttpErrorResponse } from '@angular/common/http';
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import {
  CustomizeSearchResultViewComponent
} from '@root/search/components/customize-search-result-view/customize-search-result-view.component';
import { ModalType } from '@shared/models/modal.model';
import { ActionName } from '@shared/models/effects-names.model';
import { PreferenceService } from '@root/search/services/preference.service';

const ERROR_MESSAGE_SAVE_GRID = 'Grid headers were not updated. Try again later or contact your administrator.';
const ERROR_MESSAGE_GRT_GRID = 'Can\'t get custom grid headers. Try again later or contact your administrator.';
const SUCCESS_MESSAGE_SAVE_GRID = 'Your table has been updated.';
const ERROR_MESSAGE_SAVE_CARD_ATT = 'Cards attributes were not updated. Try again later.';
const ERROR_MESSAGE_GRT_CARD_ATT = 'Can\'t get cards attributes. Try again later.';
const SUCCESS_MESSAGE_SAVE_CARD_ATT = 'Your changes have been saved.';

@Injectable()
export class CustomHeadersEffects {

  constructor(private readonly actions$: Actions,
              private readonly store$: Store<AppState>,
              private readonly customHeadersService: CustomHeadersService,
              private readonly preferenceService: PreferenceService,
              private readonly ribbonService: RibbonService,
              private readonly spinnerOverlayService: SpinnerOverlayService,
              private readonly dialog: MatDialog) {
  }

  getGridCustomHeaders$: Observable<UserActions.GetCustomGridHeadersSuccess | UserActions.GetCustomGridHeadersFail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.OPEN_GRID_CUSTOMIZE_HEADERS),
      this.spinnerOverlayService.wrapRequest(() => {
        return this.customHeadersService.getGridHeaders().pipe(
          catchError((err: HttpErrorResponse) => of(err))
        );
      }, ActionName.CUSTOM_HEADERS),
      this.mapGetGridCustomHeadersResp()
    );
  });

  getCardsCustomAttributes$: Observable<UserActions.GetCustomCardsAttributesSuccess | UserActions.GetCustomCardsAttributesFail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.OPEN_CARDS_CUSTOMIZE_ATTRIBUTES),
      this.spinnerOverlayService.wrapRequest(() => {
        return this.customHeadersService.getCardsAttributes().pipe(
          catchError((err: HttpErrorResponse) => of(err))
        );
      }, ActionName.OPEN_CARDS_CUSTOMIZE_ATTRIBUTES),
      this.mapCardsCustomAttributesResp()
    );
  });

  openGridCustomModal$: Observable<UserActions.SaveNewCustomGridHeaders> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.GET_CUSTOM_GRID_HEADERS_SUCCESS),
        withLatestFrom(
          this.store$.pipe(select(selectGridHeaders)),
          this.store$.pipe(select(selectTablePreference)),
          this.store$.pipe(select(
            selectPreferenceValue(PreferenceCode.PG_PROPERTY_GRID, PreferenceCode.PREF_RL_PROP_GRID_MAX_COLUMNS)))
        ),
        switchMap(([_, gridHeaders, tablePreference, maxColumns]) => {
          const dialogRef = this.dialog.open(CustomizeSearchResultViewComponent, {
            panelClass: ModalType.LARGE,
            data: {
              title: 'Customize Table',
              headers: gridHeaders,
              preference: tablePreference,
              maxItemNumber: parseInt(maxColumns, 10)
            }
          } as MatDialogConfig);
          return dialogRef.afterClosed();
        }),
        filter((data: Array<DataElement>) => !!(data && data.length)),
        map((data: Array<DataElement>) => new UserActions.SaveNewCustomGridHeaders(data))
      );
  });

  openCardsCustomizeModal$: Observable<UserActions.SaveCustomCardsAttributes> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.GET_CUSTOM_CARDS_ATTRIBUTES_SUCCESS),
        withLatestFrom(
          this.store$.pipe(select(selectCardsAttributes)),
          this.store$.pipe(select(selectCardsPreference))
        ),
        switchMap(([_, attributes, preference]) => {
          const dialogRef = this.dialog.open(CustomizeSearchResultViewComponent, {
            panelClass: ModalType.LARGE,
            data: {
              title: 'Customize Cards',
              headers: attributes,
              preference: preference,
              // @TODO: get this number from response when it will be added
              maxItemNumber: 3
            }
          } as MatDialogConfig);
          return dialogRef.afterClosed();
        }),
        filter((data: Array<DataElement>) => !!(data && data.length)),
        map((data: Array<DataElement>) => new UserActions.SaveCustomCardsAttributes(data))
      );
  });

  reorderCustomGridHeaders$:
    Observable<UserActions.SaveNewCustomGridHeadersSuccess | UserActions.SaveNewCustomGridHeadersFail> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.REORDER_CUSTOM_GRID_HEADERS),
        switchMap((actionData: UserActions.SaveNewCustomGridHeaders) => {
          return this.customHeadersService.saveGridHeaders(actionData.payload)
            .pipe(
              catchError((err: HttpErrorResponse) => of(err)),
              this.mapSaveGridCustomHeadersResp(false)
            );
        })
      );
  });

  saveCustomGridHeaders$: Observable<UserActions.SaveNewCustomGridHeadersSuccess | UserActions.SaveNewCustomGridHeadersFail | SearchBoardActions.ExecuteLastSearch> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.SAVE_NEW_CUSTOM_GRID_HEADERS),
        this.spinnerOverlayService.wrapRequest((actionData: UserActions.SaveNewCustomGridHeaders) => {
          return this.customHeadersService.saveGridHeaders(actionData.payload)
            .pipe(
              catchError((err: HttpErrorResponse) => of(err)),
              this.mapSaveGridCustomHeadersResp(true),
              switchMap((action: UserActions.SaveNewCustomGridHeadersSuccess | UserActions.SaveNewCustomGridHeadersFail) => {
                if (action instanceof UserActions.SaveNewCustomGridHeadersSuccess) {
                  return [action, new SearchBoardActions.ExecuteLastSearch()];
                }
                return [action];
              })
            );
        }, ActionName.SAVE_CUSTOM_HEADERS)
      );
  });

  updateGridColumnSizes$: Observable<void> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.UPDATE_GRID_COLUMN_SIZES),
        withLatestFrom(this.store$.pipe(select(selectTemplate(TemplateCode.TMPL_PROPERTY_GRID_DISPLAY_ELEMENTS)))),
        switchMap(([action, template]: [UserActions.UpdateGridColumnSizes, Template]) => {
          return this.preferenceService.updateTemplate({
            componentId: ComponentCode.COMP_GRID,
            template: {
              ...template,
              dataElementList: action.payload,
            }
          });
        })
      );
  }, {dispatch: false});

  saveCustomCardsAttributes$: Observable<UserActions.SaveCustomCardsAttributesSuccess
    | UserActions.SaveCustomCardsAttributesFail
    | SearchBoardActions.ExecuteLastSearch> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.SAVE_CUSTOM_CARDS_ATTRIBUTES),
      this.spinnerOverlayService.wrapRequest((actionData: UserActions.SaveCustomCardsAttributes) => {
        return this.customHeadersService.saveCardsAttributes(actionData.payload)
          .pipe(
            catchError((err: HttpErrorResponse) => of(err)),
            this.mapSaveCardsCustomAttributesResp()
          );
      }, ActionName.SAVE_CUSTOM_CARDS_ATTRIBUTES)
    );
  });

  private mapGetGridCustomHeadersResp() {
    return map((getGridHeaderResponse: Template) => {
      if (getGridHeaderResponse && getGridHeaderResponse.childTemplateList) {
        return new UserActions.GetCustomGridHeadersSuccess(getGridHeaderResponse);
      }
      this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, ERROR_MESSAGE_GRT_GRID);
      return new UserActions.GetCustomGridHeadersFail();
    });
  }

  private mapSaveGridCustomHeadersResp(showRibbon: boolean) {
    return map((saveGridResponse: SaveGridHeadersResponse) => {
      if (saveGridResponse && Array.isArray(saveGridResponse)) {
        if (showRibbon) {
          this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, SUCCESS_MESSAGE_SAVE_GRID);
        }
        return new UserActions.SaveNewCustomGridHeadersSuccess(saveGridResponse);
      }
      if (showRibbon) {
        this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, ERROR_MESSAGE_SAVE_GRID);
      }
      return new UserActions.SaveNewCustomGridHeadersFail();
    });
  }

  private mapCardsCustomAttributesResp() {
    return map((getResponse: Template) => {
      if (getResponse && getResponse.childTemplateList) {
        return new UserActions.GetCustomCardsAttributesSuccess(getResponse);
      }
      this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, ERROR_MESSAGE_GRT_CARD_ATT);
      return new UserActions.GetCustomCardsAttributesFail();
    });
  }

  private mapSaveCardsCustomAttributesResp() {
    return switchMap((saveResponse: SaveGridHeadersResponse) => {
      if (saveResponse && Array.isArray(saveResponse)) {
        this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, SUCCESS_MESSAGE_SAVE_CARD_ATT);
        return [
          new UserActions.SaveCustomCardsAttributesSuccess(saveResponse),
          new SearchBoardActions.ExecuteLastSearch()
        ];
      }
      this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, ERROR_MESSAGE_SAVE_CARD_ATT);
      return [
        new UserActions.SaveCustomCardsAttributesFail()
      ];
    });
  }
}
